const DigitalPayPaymentErrorState = require('./services/digital-pay-payment-error-state');

const {
    filter, find, first, replace, map, size, sumBy,
} = require('lodash');

const PLACING_ORDER_STATE_ID = 'PlacingOrder';
/**
 * DigitalPay Main Payment Component.
 * Note 1: Avoid adding payment type specific concerns in this component.
 *         i.e. This component does not need to know about specific payment instrument types (e.g. giftCard or Paypal)
 *         All payment type specific logic should be part of the instruments component.
 */
class PaymentDigitalPayController {
    constructor($element, $q, $state, settings, announcerService, digitalPayPaymentErrorDisplayService, paymentService, loadingIndicatorService, checkoutStepService, checkoutService, digitalPayIframeEventHandler, digitalPayPaymentDistributionService, digitalPayPrepaymentActionsService, digitalPayDisablePaymentStateService, digitalPayPaymentErrorStateService, digitalPayErrorMessageGenerator) {
        this.$element = $element;
        this.$q = $q;
        this.$state = $state;
        this.settings = settings;
        this.announcerService = announcerService;
        this.digitalPayPaymentErrorDisplayService = digitalPayPaymentErrorDisplayService;
        this.paymentService = paymentService;
        this.loadingIndicatorService = loadingIndicatorService;
        this.checkoutStepService = checkoutStepService;
        this.checkoutService = checkoutService;
        this.iframeEventHandler = digitalPayIframeEventHandler;
        this.paymentDistributionService = digitalPayPaymentDistributionService;
        this.prePaymentActionsService = digitalPayPrepaymentActionsService;
        this.disablePaymentStateService = digitalPayDisablePaymentStateService;
        this.digitalPayPaymentErrorStateService = digitalPayPaymentErrorStateService;
        this.digitalPayErrorMessageGenerator = digitalPayErrorMessageGenerator;
    }

    $onInit() {
        this.isCheckoutInitialised = false;
        this.isCvvDialogEnabled = false;
        this.isCreditCardSelected = false;
        this.iframeEventHandler.init();
        this.prePaymentActionsService.init();
        this.disablePaymentStateService.init();
        this.digitalPayPaymentErrorStateService.init();

        this.prePaymentActionDefinitions = {
            addAction: this.prePaymentActionsService.addAction.bind(this.prePaymentActionsService),
            removeAction: this.prePaymentActionsService.removeAction.bind(this.prePaymentActionsService),
        };

        this.paymentStateDefinitions = {
            addDisablePaymentState: this.disablePaymentStateService.addDisablePaymentState.bind(this.disablePaymentStateService),
            removeDisablePaymentState: this.disablePaymentStateService.removeDisablePaymentState.bind(this.disablePaymentStateService),
        };

        this.shouldShowPaypalPlaceOrderButton = false;

        this.initialiseBalanceToPay().then(() => {
            this.isCheckoutInitialised = true;
        });
    }

    initialiseBalanceToPay() {
        const initBalanceStateKey = 'BalanceInitialisationStateKey';
        this.paymentStateDefinitions.addDisablePaymentState(initBalanceStateKey);
        return this.checkoutService.getCheckoutModel().then((data) => {
            this.paymentDistributionService.init(data.Model.Order.BalanceToPay);
            this.paymentStateDefinitions.removeDisablePaymentState(initBalanceStateKey);
        });
    }

    $onDestroy() {
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();
        this.iframeEventHandler.dispose();
        this.prePaymentActionsService.removeAllActions();
        this.digitalPayPaymentErrorStateService.clearErrors();
    }

    /**
     * Notifies when account password field should be enabled or disabled.
     * @param {boolean} value
     */
    notifyOnAccountPasswordFieldLoaded(value) {
        this.shouldShowOnAccountPasswordField = value;
    }

    /**
     * Notifies when child instrument components are in manage mode.
     * @param {*boolean} state
     */
    notifyWhenChildIsInManageMode(state) {
        // The field here is an additional field used for showing the account password textbox
        this.anyChildIsInManageMode = state;
    }

    notifyWhenCreditCardSelectionChanged(value) {
        this.isCreditCardSelected = value;
    }

    shouldShowCheckoutMessage() {
        return this.isCreditCardSelected && this.settings.payments.enablePostPickPay && this.settings.payments.postPickPayOrderCheckoutMessage;
    }

    /**
     * Notifies when account password field should be reset
     */
    notifyPasswordReset() {
        this.resetPassword();
    }

    shouldDisablePayment() {
        return this.disablePaymentStateService.hasDisablePaymentStates() || this.hasNotFulfilledPaymentAmount() || this.hasExceededBasketAmount();
    }

    getRemainingAmount() {
        return this.paymentDistributionService.getRemainingAmount();
    }

    hasNotFulfilledPaymentAmount() {
        return this.getRemainingAmount() > 0 && !this.paymentDistributionService.hasSingleSelectType();
    }

    hasExceededBasketAmount() {
        return this.getRemainingAmount() < 0;
    }

    hasOutstandingAmount() {
        return this.paymentDistributionService.hasPayments() && this.hasNotFulfilledPaymentAmount();
    }

    setPasswordError(isError) {
        const target = this.$element.find('.onAccountPasswordInput')[0];
        if (!target) return;
        if (isError) {
            target.style.background = '#FFECEE';
            target.style.border = '1px solid #D0021B';
        } else {
            target.style.removeProperty('background');
            target.style.removeProperty('border');
        }
    }

    clearPasswordError() {
        this.setPasswordError(false);
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();
    }

    resetPassword() {
        this.accountPassword = undefined;
    }

    /**
     * For clearing errors and resetting prepayment states
     */
    resetPaymentStates() {
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();
        this.setPasswordError(false);
        this.hasPrePaymentActionFailures = false;
        this.hasPrePaymentValidationFailure = false;
        this.shouldRemoveNewlyAddedInstruments = false;
        this.isOrderDeclined = false;
        this.digitalPayErrorMessageGenerator.setPaymentErrorMode(false);
    }

    placeOrder() {
        if (this.shouldDisablePayment()) {
            this.announcerService.announce(replace(this.settings.digitalPayScreenReaderMessages.placeOrderInvalidDisableMessage, '{0}', this.disablePaymentStateService.getReasonsWhyDisabled()));
            return;
        }

        this.paymentStateDefinitions.addDisablePaymentState(PLACING_ORDER_STATE_ID, this.settings.digitalPayScreenReaderMessages.placeOrderProcessingDisableMessage);
        this.resetPaymentStates();
        const prePaymentActions = this.prePaymentActionsService.getAllActions();

        const prePaymentPromises = [];
        prePaymentActions.forEach((action) => {
            prePaymentPromises.push(action());
        });

        this._startPaymentSpinner();

        // Once all prepayment promises are resolved make the payment.
        this.$q.all(prePaymentPromises)
            .then((resolvedPromises) => {
                const payments = resolvedPromises.filter(resolvedPromise => resolvedPromise && resolvedPromise.paymentInstrumentId);

                this._startPaymentSpinner();
                const paymentsPayload = this.buildPaymentPayload(payments);

                const isSplitPayments = size(paymentsPayload.payments) > 1 || (size(paymentsPayload.payments) === 1 && this.accountPassword && paymentsPayload.accountAmount);
                this.digitalPayErrorMessageGenerator.setPaymentErrorMode(isSplitPayments);

                this.submitDigitalPay(paymentsPayload);
            })
            .catch((error) => {
                this.loadingIndicatorService.finishLoading();
                this.paymentStateDefinitions.removeDisablePaymentState(PLACING_ORDER_STATE_ID);
                this.hasPrePaymentActionFailures = true;
                if (error && error.errorMessage) {
                    this.digitalPayPaymentErrorDisplayService.pushGlobalError(error.errorMessage);
                }
            });
    }

    /**
     * Processes DigitalPay payments.
     * @param {Array} payments
     */
    buildPaymentPayload(payments) {
        const mergedPayments = {};
        payments.forEach((payment) => {
            const existingPayment = mergedPayments[payment.paymentInstrumentId];
            if (existingPayment) {
                mergedPayments[payment.paymentInstrumentId] = {
                    paymentInstrumentId: existingPayment.paymentInstrumentId,
                    amount: (existingPayment.amount || 0.0) + (payment.amount || 0.0),
                    stepUpToken: existingPayment.stepUpToken || payment.stepUpToken,
                };
            } else {
                mergedPayments[payment.paymentInstrumentId] = {
                    paymentInstrumentId: payment.paymentInstrumentId,
                    amount: payment.amount,
                    stepUpToken: payment.stepUpToken,
                };
            }
        });

        const paymentsPayload = {
            payments: map(mergedPayments, value => value)
                .filter(payment => payment.amount > 0.0),
        };

        paymentsPayload.accountAmount = this.shouldShowOnAccountPasswordField
            ? this.paymentDistributionService.getOrderTotal() - sumBy(payments, payment => payment.amount)
            : 0.0;

        const paymentWithDeviceData = find(payments, p => p.deviceData != null);
        paymentsPayload.deviceData = paymentWithDeviceData && paymentWithDeviceData.deviceData;

        return paymentsPayload;
    }

    /**
     * Submits DigitalPay payments.
     */
    submitDigitalPay(paymentsPayload) {
        paymentsPayload.accountPassword = this.accountPassword;
        paymentsPayload.accountAmount = paymentsPayload.accountAmount;

        this.paymentService.submitDigitalPay(paymentsPayload)
            .then(response => this._handlePaymentSuccess(response))
            .then(response => this._handlePaymentInstrumentFailures(response, paymentsPayload))
            .then(response => this._handleGenericPaymentFailure(response))
            .then(response => this._handleOrderPlacementErrors(response))
            .then((response) => {
                if (!response.Result) {
                    this.loadingIndicatorService.finishLoading();
                }
                return response;
            })
            .catch((error) => {
                this.loadingIndicatorService.finishLoading();
                this.digitalPayPaymentErrorDisplayService.pushGlobalError(error.ErrorMessage);
            })
            .finally(() => {
                this.paymentStateDefinitions.removeDisablePaymentState(PLACING_ORDER_STATE_ID);
            });
    }

    showCvvConfirmationDialog(paymentsPayload, paymentInstrumentId) {
        // We need to specifically turn on this dialog otherwise it captures iframe events
        this.isCvvDialogEnabled = true;
        this.cvvConfirmationDialog.show().then((dialogResponse) => {
            if (dialogResponse.success) {
                this.digitalPayPaymentErrorDisplayService.clearGlobalError();
                this._startPaymentSpinner();
                const paymentToAddStepUpToken = find(paymentsPayload.payments, payment => payment.paymentInstrumentId === paymentInstrumentId);
                if (paymentToAddStepUpToken) {
                    paymentToAddStepUpToken.stepUpToken = dialogResponse.stepUpToken;
                }
                this.submitDigitalPay(paymentsPayload);
            } else {
                this.digitalPayPaymentErrorDisplayService.pushGlobalError(dialogResponse.ErrorMessage);
            }
        }).finally(() => {
            this.isCvvDialogEnabled = false;
        });
    }

    closedCvvConfirmationDialog() {
        this.shouldRemoveNewlyAddedInstruments = true;
    }

    backToReview() {
        this.checkoutStepService.resetStepTo('review');
    }

    /**
     * Handles case where payment request is successful.
     * @param {Object} response Payment Response
     */
    _handlePaymentSuccess(response) {
        // Payment Successful.
        if (response.Result) {
            // Direct navigation is used instead of state transition in order to get the new state of a shopper's cart.
            window.location.href = `/shop/confirmation?orderId=${response.PlacedOrderId}`;
        }

        return response;
    }

    /**
     * Handles case where payment request fails with failures with specific instruments.
     * @param {Object} response Payment Response
     * @param {Object} paymentsPayload Payment payloads request
     */
    _handlePaymentInstrumentFailures(response, paymentsPayload) {
        // Handle payment specific errors.
        if (!response.Result && response.PaymentResponses && response.PaymentResponses.length) {
            const checkInstrumentError = (value, type) => value.PaymentInstrumentType === type && value.ErrorCode;
            const creditCardError = first(filter(response.PaymentResponses, value => checkInstrumentError(value, 'CreditCard')));
            if (creditCardError && creditCardError.StepUp && creditCardError.StepUp.Url) {
                this.cvvUrl = creditCardError.StepUp.Url;
                this.showCvvConfirmationDialog(paymentsPayload, creditCardError.PaymentInstrumentId);
            } else {
                this.digitalPayPaymentErrorStateService.addErrors(response.PaymentResponses
                    .filter(paymentResponse => paymentResponse.ErrorCode)
                    .map(paymentResponse => new DigitalPayPaymentErrorState(
                        paymentResponse.PaymentInstrumentId,
                        paymentResponse,
                    )));
                this.shouldRemoveNewlyAddedInstruments = true;
            }
        }

        return response;
    }

    /**
     * Handles case where payment request fails with a generic error.
     * @param {Object} response
     */
    _handleGenericPaymentFailure(response) {
        // Handle generic Payment failures.
        if (!response.Result) {
            if (response.ErrorCode &&
                response.ErrorCode !== 'InvalidPassword' &&
                response.ErrorCode !== this.settings.digitalPayErrorCodes.creditCardStepUpTokenRequired) {
                if (response.ErrorCode === this.settings.digitalPayErrorCodes.unknown) {
                    this.shouldRemoveNewlyAddedInstruments = true;
                } else {
                    this.digitalPayPaymentErrorDisplayService.pushGlobalError();
                }
            }
        }

        return response;
    }

    /**
     * Handles case where payment request fails with OrderPlacement errors.
     * @param {Object} response
     */
    _handleOrderPlacementErrors(response) {
        // Handle Order Placement Errors.
        const orderPlacementError = first(response.OrderPlacementErrors);
        if (!response.Result && orderPlacementError) {
            let errorMessage = response.ErrorMessage
                ? response.ErrorMessage
                : response.OrderPlacementErrors[0].Message;

            if (orderPlacementError.Type === this.settings.digitalPayOrderPlacementErrorCodes.timeout) {
                // Clear global error as the error context is already part of the dialog.
                this.digitalPayPaymentErrorDisplayService.clearGlobalError();
                this.timeoutErrorMessage = orderPlacementError.Message;
                this.confirmationDialog.show();
            } else if (orderPlacementError.Type === this.settings.digitalPayOrderPlacementErrorCodes.deliveryWindow) {
                // Clear global error as the error context is already part of the dialog.
                this.digitalPayPaymentErrorDisplayService.clearGlobalError();
                this.deliveryTimeoutConfirmationDialog.show()
                    .finally(() => this.checkoutStepService.canReviewOrder(false));
            } else if (orderPlacementError.Type === this.settings.digitalPayOrderPlacementErrorCodes.invalidPassword) {
                this.setPasswordError(true);
                this.hasPrePaymentValidationFailure = true;
                errorMessage = this.settings.digitalPayError.onAccountInvalidPasswordMessage;
            } else if (orderPlacementError.Type === this.settings.digitalPayOrderPlacementErrorCodes.declined) {
                this.shouldRemoveNewlyAddedInstruments = true;
                this.isOrderDeclined = true;
                this.digitalPayPaymentErrorStateService.addErrors(response.PaymentResponses
                    .filter(paymentResponse => !paymentResponse.ErrorCode)
                    .map(paymentResponse => new DigitalPayPaymentErrorState(
                        paymentResponse.PaymentInstrumentId,
                        this.addDeclinedErrorDetails(paymentResponse),
                    )));
            }

            this.digitalPayPaymentErrorDisplayService.pushGlobalError(errorMessage);
        }

        return response;
    }

    addDeclinedErrorDetails(paymentResponse) {
        paymentResponse.ErrorCode = this.settings.digitalPayOrderPlacementErrorCodes.declined;
        paymentResponse.ErrorMessage = '';

        return paymentResponse;
    }

    _startPaymentSpinner() {
        this.loadingIndicatorService.startLoading(true, 'Processing your payment', false);
    }
}

PaymentDigitalPayController.$inject = ['$element', '$q', '$state', 'Settings', 'AnnouncerService', 'DigitalPayPaymentErrorDisplayService', 'PaymentService', 'LoadingIndicatorService', 'CheckoutStepService', 'CheckoutService', 'DigitalPayIframeEventHandler', 'DigitalPayPaymentDistributionService', 'DigitalPayPrepaymentActionsService', 'DigitalPayDisablePaymentStateService', 'DigitalPayPaymentErrorStateService', 'DigitalPayErrorMessageGenerator'];

const paymentDigitalPayComponentDefinition = {
    controller: PaymentDigitalPayController,
    template: require('./payment-digital-pay.component.html').trim(),
};

module.exports = {
    PaymentDigitalPayController,
    paymentDigitalPayComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/digitalpay/payment-digital-pay.component.js